<script setup lang="ts">
import { computed } from "vue";
import { openSpeckleModal } from "@features/Speckle";
import Badge from "@/components/Reusables/Badge/Badge.vue";

// Information, not a control, so read-only lists show it too. On the reader's own row it opens the
// Speckle settings, where they create or connect the account, or sync their projects, themselves.
const props = defineProps<{
  status: "noAccount" | "notOnModel";
  isSelf: boolean;
}>();

const label = computed(() => (props.status === "noAccount" ? "No Speckle account" : "Not on 3D model"));

const hint = computed(() => {
  if (props.status === "noAccount") {
    return props.isSelf
      ? "Without a Speckle account you can't open the 3D model or publish to it. Click to set up Speckle."
      : "Without a Speckle account they can't open the 3D model or publish to it. They'll be asked to set up Speckle when they open the project.";
  }
  return props.isSelf
    ? "You haven't been added to this project's 3D model yet. Click to open Speckle and run Sync projects."
    : "They haven't been added to this project's 3D model yet. They'll be offered Sync projects when they open the project.";
});
</script>

<template>
  <button
    v-if="isSelf"
    v-tooltip="hint"
    type="button"
    class="rounded-full focus-visible:ring-2 focus-visible:ring-app-blue-600 focus-visible:outline-none"
    data-speckle-flag
    :data-speckle-status="status"
    :aria-label="`${label}. Open Speckle settings`"
    @click="openSpeckleModal()">
    <Badge
      variant="warning"
      size="sm">
      {{ label }}
    </Badge>
  </button>
  <span
    v-else
    v-tooltip="hint"
    class="inline-flex shrink-0"
    data-speckle-flag
    :data-speckle-status="status">
    <Badge
      variant="warning"
      size="sm">
      {{ label }}
    </Badge>
  </span>
</template>
